/**
 * LES AFFICHAGES DE LA CARTE — PC§27 (contrat dicté par le fondateur le
 * 19/09/2026) : « affichages cadastral, satellite, orthophoto, 3D », rappelé
 * sur capture le 12/10/2026 (« pourquoi il y a toujours cette vue ? »). D.1516.
 *
 * ⚠️ TABLE PURE, SANS DOM NI MAPLIBRE — même scission que trames-etat.ts /
 * trames-peinture.ts : le 4e tsc du pré-vol n'a pas la lib DOM, et les bancs
 * importent cette table nue.
 *
 * ⚠️ UN AFFICHAGE SANS MATIÈRE NE S'OFFRE PAS, ET SON ABSENCE SE DIT. Mesuré
 * le 12/10/2026 : AUCUNE imagerie n'existe dans l'écosystème — le moteur
 * satellite de Maps est du code mort (importé par personne, table absente en
 * base, aucun téléchargeur), et ce dépôt s'interdit toute tuile externe
 * (règle de souveraineté, carte.tsx). Un bouton qui mène à un fond vide se
 * lirait « la carte est cassée » — la famille exacte des huit capacités
 * déclarées n'allumant aucun chemin (leçon Pay). Le jour où une imagerie
 * SOUVERAINE est versée (route raster servie par CE dépôt, sur le modèle de
 * /relief), `offert` passe à vrai ici, et nulle part ailleurs.
 *
 * ⚠️ L'AFFICHAGE EST ORTHOGONAL AU PLAN (état/qualité) : l'affichage choisit
 * ce qui est MONTRÉ (couches, caméra) ; le plan choisit comment les limites
 * sont PEINTES. Les fondre en un seul enum ferait perdre l'un en choisissant
 * l'autre.
 */

export type Affichage = 'cadastral' | 'trois_d' | 'satellite' | 'orthophoto';

export interface ModeAffichage {
  cle: Affichage;
  libelle: string;
  /** Faux = le bouton se montre ÉTEINT, avec son motif en clair. */
  offert: boolean;
  /** Pourquoi l'affichage n'est pas offert — null quand il l'est. */
  motif: string | null;
  /** Couches passées en `visibility: none` quand ce mode est actif. */
  couchesEteintes: readonly string[];
  /** Inclinaison de caméra, posée par `jumpTo` (D.951 : jamais d'animation). */
  pitch: number;
}

/**
 * ⚠️ `sections-surface` NE S'ÉTEINT JAMAIS : elle est la surface CLIQUABLE des
 * sections (opacité 0,012) — `queryRenderedFeatures` ne voit pas une couche
 * masquée, et l'éteindre casserait toute sélection de section sans un mot.
 */
const BATI = [
  'batiments', 'batiments-contour', 'batiments-contour-presume',
  'batiments-3d', 'batiments-3d-presume',
] as const;

export const AFFICHAGES: readonly ModeAffichage[] = [
  {
    cle: 'cadastral', libelle: 'Cadastral', offert: true, motif: null,
    // La lecture de PLAN : les limites d'abord, le bâti s'efface, la vue à
    // plat — « un plan cadastral se lit à plat » (décision du 19/09).
    couchesEteintes: BATI,
    pitch: 0,
  },
  {
    cle: 'trois_d', libelle: '3D', offert: true, motif: null,
    // La vue volumétrique : tout est montré, la caméra s'incline — les
    // extrusions existantes (z15+) prennent corps. On QUITTE ce mode pour
    // instruire : une parcelle vue en oblique change de forme apparente.
    couchesEteintes: [],
    pitch: 45,
  },
  {
    cle: 'satellite', libelle: 'Satellite', offert: false,
    motif: 'aucune scène satellite souveraine n\'est versée — le registre ne '
      + 'stocke aucune imagerie, et la règle du dépôt refuse toute tuile '
      + 'externe ; cette vue s\'allumera au premier versement',
    couchesEteintes: [],
    pitch: 0,
  },
  {
    cle: 'orthophoto', libelle: 'Orthophoto', offert: false,
    motif: 'aucune orthophotographie de l\'État n\'est versée au registre — '
      + 'cette vue s\'allumera au premier versement',
    couchesEteintes: [],
    pitch: 0,
  },
];

export const AFFICHAGE_DEFAUT: Affichage = 'cadastral';

/** Toutes les couches qu'un mode ou un autre commute — pour les RALLUMER en quittant. */
export const COUCHES_COMMUTEES: readonly string[] =
  [...new Set(AFFICHAGES.flatMap((m) => m.couchesEteintes))];

export function modeAffichage(cle: Affichage): ModeAffichage {
  return AFFICHAGES.find((m) => m.cle === cle) ?? AFFICHAGES[0]!;
}
